import { ensureCurrentUser } from './data';

export const PROVIDER_MAIN_USER_TYPE = 'provider-main';

const yn = complete => (complete ? 'Y' : 'N');

export const isProviderMainUser = currentUser => {
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  return userType === PROVIDER_MAIN_USER_TYPE;
};

/** True when Stripe account data reports payouts are enabled. */
export const isStripePayoutsEnabled = stripeAccount =>
  stripeAccount?.attributes?.stripeAccountData?.payouts_enabled === true;

/** True when the account includes expanded Stripe Account Data (from sdk.stripeAccount.fetch). */
export const hasStripeAccountData = stripeAccount =>
  !!stripeAccount?.attributes?.stripeAccountData;

/**
 * Whether to apply stripeAccount from currentUser.show include into the duck.
 * Thin relationship includes often omit stripeAccountData; applying them would
 * wipe a prior sdk.stripeAccount.fetch() and make payouts appear incomplete.
 *
 * @param {Object|null} existing - Current duck stripeAccount
 * @param {Object|null} incoming - stripeAccount from user include
 * @returns {boolean}
 */
export const shouldApplyStripeAccountFromUserInclude = (existing, incoming) => {
  if (!incoming) {
    return false;
  }
  if (hasStripeAccountData(existing) && !hasStripeAccountData(incoming)) {
    return false;
  }
  return true;
};

/**
 * Evaluate provider requirement criteria (same rules as /provider-requirements).
 *
 * @param {Object} currentUser - Denormalised currentUser
 * @param {{ hasListings?: boolean, stripeAccount?: Object }} options
 * @returns {{ photo: boolean, wwwc: boolean, chc: boolean, abn: boolean, stripe: boolean, listing: boolean, allComplete: boolean }}
 */
export const getProviderRequirementsStatus = (
  currentUser,
  { hasListings = false, stripeAccount = null } = {}
) => {
  if (!currentUser) {
    return {
      photo: false,
      wwwc: false,
      chc: false,
      abn: false,
      stripe: false,
      listing: false,
      allComplete: false,
    };
  }

  const user = ensureCurrentUser(currentUser);
  const { publicData, protectedData } = user?.attributes?.profile || {};
  const profileImage = user?.profileImage;

  const photo = !!(profileImage && profileImage.id);
  const wwwc = !!protectedData?.wwwcFileUpload;
  const chc = !!protectedData?.chcFileUpload;
  const abn = !!publicData?.prov_abn;
  const stripe = isStripePayoutsEnabled(stripeAccount);
  const listing = !!hasListings;

  const allComplete = photo && wwwc && chc && abn && stripe && listing;

  return { photo, wwwc, chc, abn, stripe, listing, allComplete };
};

/** First five requirements only (profile + Stripe), excluding published listings. */
export const hasCompletedAllProfileRequirements = (currentUser, options = {}) => {
  const { listing: _listing, allComplete: _allComplete, ...profileOnly } = getProviderRequirementsStatus(
    currentUser,
    { ...options, hasListings: true }
  );
  return profileOnly.photo && profileOnly.wwwc && profileOnly.chc && profileOnly.abn && profileOnly.stripe;
};

/** All six requirements including at least one published listing. */
export const hasCompletedAllProviderRequirements = (currentUser, options = {}) => {
  return getProviderRequirementsStatus(currentUser, options).allComplete;
};

/** Number of unmet criteria out of the six provider requirements. */
export const countIncompleteProviderRequirements = (currentUser, options = {}) => {
  const { allComplete: _allComplete, ...criteria } = getProviderRequirementsStatus(
    currentUser,
    options
  );
  return Object.values(criteria).filter(complete => !complete).length;
};

/**
 * Log requirement status to the console for manual testing.
 *
 * @param {Object} status - Criteria booleans (+ allComplete, providerRequirementsComplete)
 * @param {{ source?: string }} options
 */
export const logProviderRequirementsCheck = (status, { source } = {}) => {
  const prefix = source ? `[provider-requirements][${source}] ` : '[provider-requirements] ';
  const flagValue =
    typeof status.providerRequirementsComplete === 'boolean'
      ? status.providerRequirementsComplete
      : status.allComplete;
  const line = `${prefix}Check Photo: ${yn(status.photo)}, WWCC: ${yn(status.wwwc)}, CHC: ${yn(
    status.chc
  )}, ABN: ${yn(status.abn)}, Stripe: ${yn(status.stripe)}, Listing: ${yn(
    status.listing
  )} | AllComplete: ${yn(status.allComplete)} | Flag: ${yn(flagValue)}`;

  // eslint-disable-next-line no-console
  console.log(line);
};

export const logProviderRequirementsSkipped = (reason, { source } = {}) => {
  const prefix = source ? `[provider-requirements][${source}] ` : '[provider-requirements] ';
  // eslint-disable-next-line no-console
  console.log(`${prefix}Skipped: ${reason}`);
};

export const checkAndLogProviderRequirements = (currentUser, options = {}) => {
  const { source, ...statusOptions } = options;
  const status = getProviderRequirementsStatus(currentUser, statusOptions);
  logProviderRequirementsCheck(status, { source });
  return status;
};
